import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ICityTaxAutoFile, IGiveaway, IGiveawayAuto, IImportTaxesAuto } from "../../../../interfaces";
import useAuth from "../../../../hooks/useAuth";
import { SubmitHandler, useForm } from "react-hook-form";
import { EmitError } from "../../../../utils/EmitError";
import GiveawaysAutoService from "../../../../services/http/services/GiveawaysAutoService";
import { AxiosError } from "axios";
import { isBefore } from "date-fns";
import { UploadFile } from "antd";
import { EmitSuccessAlert } from "../../../../utils/EmitSuccessAlert";
import CityTaxesAutoService from "../../../../services/http/services/CityTaxesAutoService";

interface IProps {
    giveaway?: IGiveaway,
    createdGiveaway?: IGiveaway,
}

interface IChangeFileProps {
    onChangeHookForm: (event: any) => void,
    fileList: UploadFile<any>[]
}

const ACCEPTED_FILE_TYPES = [
    'text/plain',
    'application/json',
]


const impostosMunicipais = ['IPTU'];

export default function useImportTaxeAutoForm({ giveaway, createdGiveaway }: IProps) {
    const refIsEditing = useRef<boolean>(Boolean(giveaway));
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [giveawaysAuto, setGiveawaysAuto] = useState<IGiveawayAuto[]>([]);
    const [taxAutoFile, setTaxAutoFile] = useState<ICityTaxAutoFile[]>([]);
    const [hasPendingImports, setHasPendingImports] = useState(false);
    const [files, setFiles] = useState<UploadFile[]>([]);
    const { activeCity } = useAuth();
    const refAbortController = useRef<AbortController | null>(null);


    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        clearErrors,
        watch,
        setError
    } = useForm<IImportTaxesAuto>({
        defaultValues: {
            tax_type: 'IPTU',
            raffle_auto_id: null,
            county_id: activeCity ? activeCity.id : undefined,
            files: []
        },
        shouldFocusError: false
    });

    const loadTaxAutoFile = useCallback(async () => {
        try {
            refAbortController.current?.abort();

            refAbortController.current = new AbortController();

            const taxAutoFileList = await CityTaxesAutoService.listCityTaxesAutoFile(activeCity?.id!, refAbortController.current.signal);
            const hasPending = taxAutoFileList.some(item => item.import_progress < 100);
            setHasPendingImports(hasPending);
            setTaxAutoFile(taxAutoFileList);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            EmitError(err, true, 'giveaways_aosdpasdpasd');
        }
    }, [activeCity?.id]);

    const handleSubmitForm: SubmitHandler<IImportTaxesAuto> = useCallback(async (data): Promise<boolean> => {
        try {
            setIsSubmiting(true);

            if (!data.raffle_auto_id) {
                setError('raffle_auto_id', {
                    message: 'Selecione o sorteio'
                });
                return false;
            }


            const importTaxe = new FormData();

            files.forEach(file => {
                if (!file.originFileObj) {
                    return;
                }
                importTaxe.append('files', file.originFileObj);
            });

            importTaxe.append('tax_type', data.tax_type);
            importTaxe.append('raffle_auto_id', String(data.raffle_auto_id));
            importTaxe.append('county_id', String(data.county_id));

            const newImportTaxeMessage = await CityTaxesAutoService.createTaxesAuto(importTaxe);
            setValue('files', []);
            setHasPendingImports(true);
            EmitSuccessAlert(newImportTaxeMessage);
            setFiles([]);
            loadTaxAutoFile();

            return true;
        } catch (err) {
            EmitError(err);
            return false;
        } finally {
            setIsSubmiting(false);
        }
    }, [files, loadTaxAutoFile, setError, setValue]);

    const dummyRequest = useCallback(({ onSuccess }: any) => {
        setTimeout(() => {
            onSuccess("ok");
        }, 0);
    }, []);

    const handleClickDownloadFile = useCallback(async (file_name: string) => {
        refAbortController.current?.abort();

        refAbortController.current = new AbortController();
        const fileUrl = await CityTaxesAutoService.downloadCityTaxesAutoFile(file_name, refAbortController.current.signal);

        const link = document.createElement('a');
        link.download = file_name;
        link.target = '_blank';
        link.href = fileUrl;
        link.click();
    }, []);

    const handleChangeFile = useCallback(({ onChangeHookForm, fileList }: IChangeFileProps) => {

        fileList.forEach(file => {
            if (file?.type && !ACCEPTED_FILE_TYPES.includes(file.type)) {
                setError('files', { message: 'Formato de arquivo inválido! O formato aceitos são .json e .txt' });
                return;
            }
        });

        clearErrors('files');
        onChangeHookForm(fileList);
        setFiles(fileList);

    }, [setError, clearErrors]);


    useEffect(() => {
        // chama a primeira vez
        loadTaxAutoFile();
    }, [loadTaxAutoFile]);

    useMemo(() => {
        if (!hasPendingImports) return;

        const interval = setInterval(() => {
            loadTaxAutoFile();
        }, 10000);

        return () => clearInterval(interval);
    }, [hasPendingImports, loadTaxAutoFile]);;

    useEffect(() => {
        const controller = new AbortController();

        async function loadTaxesAuto() {
            try {
                setIsLoading(true);

                const taxesAutoList = await GiveawaysAutoService.listGiveawaysAuto(activeCity?.id!, controller.signal);

                const now = new Date();
                const filteredGiveaways = taxesAutoList.filter(giveaway =>
                    giveaway.date && isBefore(now, new Date(giveaway.date))
                );

                setGiveawaysAuto(filteredGiveaways);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'giveaways_aosdpasdpasd');
                setIsLoading(false);
            }
        }

        loadTaxesAuto();

        return () => {
            controller.abort();
        }
    }, [activeCity]);

    return {
        refIsEditing,
        impostosMunicipais,
        handleSubmitForm,
        handleSubmit,
        control,
        isSubmiting,
        errors,
        setValue,
        watch,
        clearErrors,
        giveawaysAuto,
        ACCEPTED_FILE_TYPES,
        dummyRequest,
        handleChangeFile,
        files,
        taxAutoFile,
        handleClickDownloadFile,
        isLoading
    }
}
